import {
    Api,
    AppThemeType,
    BootstrapParameters,
    CMAPState,
    DatasetDef,
    DatasetDefaults,
    DatasetDefId, LayerDef,
    MapOfDatasetDefaults,
    RowCell,
    SelectionListener,
    ThemeType
} from "../types";
import {getApi, getAppState, getBootstrapParams, getSelectionManager} from "./AppState";
import {getAppComponent, getMapComponent} from "./Accessor";
import {AddDatasetDialog} from "../components/AddDatasetDialog";
import {Dataset} from "../data/Dataset";
import {ColorUtils} from "../util/ColorUtils";
import {isValidNumber} from "../util/CommonUtil";
import {Color} from "../util/Color";
import {DATASET_DEFAULTS} from "../datasetDefaultsDef";
import {ConnectConsoleProxy, getGeoService, setAdditionalHeader} from "@esignal/polygon-service-api";

let initialized: boolean = false;

declare const CMAPS_PKG_VERSION: string;
const pkgVersion = typeof CMAPS_PKG_VERSION === "undefined" ? "unknown" : CMAPS_PKG_VERSION;
declare const CMAPS_COMMIT_HASH: string;
const commitHash = typeof CMAPS_COMMIT_HASH === "undefined" ? "unknown" : CMAPS_COMMIT_HASH;
declare const CMAPS_BUILD_TIMESTAMP: number;
const buildTimestamp = typeof CMAPS_BUILD_TIMESTAMP === "undefined" ? undefined : CMAPS_BUILD_TIMESTAMP;


export interface DatasetEntry {
    datasetDef: DatasetDef;
    data: RowCell[][];
}


export class ApiImpl implements Api {

    constructor() {
        if (!(window as any).cmaps_api_initial_theme) {
            // register only in standalone browser mode
            // registerSysThemeChangeListener(this);
        }
    }

    private initializeCheck(): boolean {
        if (!initialized) {
            const msg = "Not initialized yet. Api should have been initialized with cmaps_api.initialize(params) first";
            console.error(msg);
            alert(msg);
            // throw Error(msg);
            // return false;
        }
        return true;
    }

    initialize(bootstrapParameters: string | BootstrapParameters): void {
        initialized = true;

        let params: BootstrapParameters;

        if (typeof bootstrapParameters === "string") {
            params = JSON.parse(bootstrapParameters) as BootstrapParameters;
        } else {
            params = bootstrapParameters as BootstrapParameters;
        }

        if (params.consoleHandler) {
            ConnectConsoleProxy.setConnectConsoleProxyHandler(params.consoleHandler);
        }

        if (params.username) {
            setAdditionalHeader("x-onyx-user", params.username);
        }
        if (params.globalUsername) {
            setAdditionalHeader("x-icesso-user", params.globalUsername);
        }

        const geoService = getGeoService();
        if (geoService) {
            geoService.initialize();
        }

        getBootstrapParams().update(params);
    }

    addDataset(dataset: DatasetDef, data: RowCell[][]) {
        if (!this.initializeCheck()) {
            return;
        }
        this.addDatasets([{ datasetDef: dataset, data }]);
    }

    addDatasets(entries: DatasetEntry[]) {
        if (!this.initializeCheck()) {
            return;
        }

        const appState = getAppState();
        appState.addDatasetEntries(entries);
        getAppComponent().showDialog(<AddDatasetDialog entries={appState.getDatasetEntriesToAdd()} />)
    }

    getState(asJson?: boolean): string | object {
        const json = getAppState().save();
        return asJson ? json : JSON.stringify(json);
    }

    setState(state: object | string) {
        const cmapsState: object = typeof state === "string" ? JSON.parse(state) : state;
        getAppState().load(cmapsState);
        getMapComponent().resetState();
        getAppComponent().refresh();
    }

    setGlobalSettings(settings: any): void {
        if (settings?.customPalettes) {
            ColorUtils.defineCustomPalettes(settings.customPalettes);
            const customPalettes = ColorUtils.getCustomPalettes();
            const updatedLayerDefs: LayerDef[] = [];
            for (const layerDef of getAppState().getLayerDefs()) {
                const idx = layerDef.config.customPaletteIndex;
                if (idx >= 0 && idx < customPalettes.length) {
                    layerDef.config.colorPalette = customPalettes[idx].map(s => Color.createFromString(s));
                    updatedLayerDefs.push(layerDef);
                }
            }

            if (updatedLayerDefs.length > 0) {
                getMapComponent().updateLayers(...updatedLayerDefs);
                getAppComponent().refresh();
            }
        }
    }

    setTheme(sysTheme: ThemeType) {
        if (!this.initializeCheck()) {
            return;
        }
        const appState = getAppState();
        appState.setAppTheme(sysTheme);
        if (appState.getThemeType() === AppThemeType.system) {
            getMapComponent()?.recreateLayers();
            getAppComponent().refresh();
        }
    }

    refreshDataset(datasetId: DatasetDefId, rows: RowCell[][], centerMap: boolean = false) {
        if (!this.initializeCheck()) {
            return;
        }

        const dataset = getAppState().getDatasetById(datasetId);
        if (dataset) {
            dataset.setData(rows);
            getMapComponent().recreateLayers();
            getAppComponent().refresh();
        } else {
            console.error("Unknown dataset " + datasetId);
        }
    }

    getVersion() {
        return pkgVersion;
    }

    getCommitHash() {
        return commitHash;
    }

    getBuildTimestamp(): number | undefined {
        return buildTimestamp;
    }

    onSelect(selection: any): void {
        // if (!this.initializeCheck()) {
        //     return;
        // }
        if (typeof selection === "object") {
            if (selection.symbol && selection.dataset && selection.data) {
                const dataset = new Dataset(selection.dataset, selection.data);
                const appState = getAppState();
                appState.clearMarkers();
                const marker = appState.selectMarker(selection.symbol, dataset);
                getAppComponent().refresh();
                getMapComponent().flyTo(marker.getDataRow().getLonLat());
            }
        }
    }

    public addSelectionListener(listener: SelectionListener) {
        getSelectionManager().addListener(listener);
    }

    public removeSelectionListener(listener: SelectionListener) {
        getSelectionManager().removeListener(listener);
    }

}
